/**
 * Error collector for runtime error tracking.
 *
 * Collects errors and warnings during generation/rendering for telemetry.
 */

interface LogEntry {
  message: string;
  timestamp: number;
  context?: Record<string, any>;
}

class ErrorCollector {
  private errors: LogEntry[] = [];
  private warnings: LogEntry[] = [];

  /**
   * Push an error to the collector.
   */
  pushError(message: string, context?: Record<string, any>): void {
    this.errors.push({
      message,
      timestamp: Date.now(),
      context,
    });

    // Also log to console for visibility
    console.error(`[ErrorCollector] ${message}`, context);
  }

  /**
   * Push a warning to the collector.
   */
  pushWarning(message: string, context?: Record<string, any>): void {
    this.warnings.push({
      message,
      timestamp: Date.now(),
      context,
    });

    console.warn(`[ErrorCollector] ${message}`, context);
  }

  /**
   * Get all collected logs and clear the collector.
   */
  getAndClear(): { errors: string[]; warnings: string[] } {
    const logs = {
      errors: this.errors.map((e) =>
        e.context ? `${e.message} (${JSON.stringify(e.context)})` : e.message,
      ),
      warnings: this.warnings.map((w) =>
        w.context ? `${w.message} (${JSON.stringify(w.context)})` : w.message,
      ),
    };

    // Clear for next run
    this.errors = [];
    this.warnings = [];

    return logs;
  }

  /**
   * Get current log counts without clearing.
   */
  getCounts(): { errors: number; warnings: number } {
    return {
      errors: this.errors.length,
      warnings: this.warnings.length,
    };
  }

  /**
   * Clear all logs.
   */
  clear(): void {
    this.errors = [];
    this.warnings = [];
  }
}

// Global singleton instance
export const errorCollector = new ErrorCollector();
